import React, { useMemo, useRef } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import useTranslation from 'next-translate/useTranslation'
import omit from 'lodash.omit'
import enGb from 'date-fns/locale/en-GB'
import ReactTooltip from 'react-tooltip'
import { every } from 'lodash'
import DateContainer from 'tennders-common/form/components/DateContainer'
import 'moment/locale/en-gb'
import GHref from 'tennders-market/common/components/element/GHref'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons'
import IconCalendar from '../../../../icons/calendar.svg'

registerLocale('en-gb', enGb)

type ValueType = string | Date

type Props = {
  value?: ValueType | ValueType[];
  initialValue?: ValueType | ValueType[];
  form ?: Record<string, any>,
  onChange?: (name: string, value: ValueType | ValueType[]) => void,
  timePicker ?: boolean,
  name ?: string,
  eClass?: string,
  className?: string,
  errors ?: Record<string, any>,
  error?: string,
  dateClass?: string,
  onlyInput?: boolean,
  label?: string,
  labelKey?: string,
  single ?: boolean,
  labelClass?: string,
  children?: React.ReactNode | React.ReactNode[],
  style?: React.CSSProperties,
  readOnly?: boolean,
  notClearable? : boolean,
  type ?: 'date' | 'time' | 'date-time' | 'datetime',
  minDate ?: Date | string,
  maxDate ?: Date | string,
  disabled ?: boolean,
  placeholder?: string,
  dateFormat?: string,
  showEasyFilters ?: boolean,
  showMonthYearPicker?: boolean
}

const GDatePicker = ({
  value, form, onChange, timePicker, name, eClass, className, errors, error, dateClass, notClearable,
  onlyInput, label, labelKey, single, labelClass, children, readOnly, showEasyFilters, initialValue = null,
  ...rest
} : Props) => {
  const ref = useRef(null)
  const { t } = useTranslation()

  const getError = () => {
    if (errors && name) {
      return errors[name]
    }
    return error
  }

  const renderInput = () => {
    let val = form && form[name] ? form[name] : (value || [])
    if (timePicker) {
      val = form && form[name] ? form[name] : (value || 0)
    }
    if (single) {
      val = form && form[name] ? new Date(form[name]) : (value && new Date(value as string))
      return (
        <DatePicker
          isClearable={!readOnly && !notClearable}
          placeholderText={rest.placeholder}
          dateFormat="dd/MM/yyyy"
          className={`${className || 'form-input w-100'}${getError() ? ' error' : ''}${readOnly ? ' disabled' : ''}`}
          selected={val}
          onChange={(date) => {
            onChange(name, date)
          }}
          readOnly={readOnly}
          locale="en-gb"
          onKeyDown={(e) => {
            e.preventDefault()
          }}
          {...rest}
        />
      )
    }

    return (
      timePicker ? (
        <DatePicker
          placeholderText={rest.placeholder}
          className={`${className || 'form-input w-100 time-picker'}${getError() ? ' error ' : ' '} `}
          selected={initialValue || val}
          onChange={(date) => {
            onChange(name, date)
          }}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          timeCaption={label}
          dateFormat="HH:mm"
          timeFormat="HH:mm"
          timeInputLabel={label}
          locale="en-gb"
          onKeyDown={(e) => {
            // e.preventDefault();
          }}
          {...rest}
        />
      ) : (
        <>
          <DatePicker
            ref={ref}
            isClearable={!readOnly && !notClearable}
            calendarContainer={showEasyFilters ? (props) => (
              <DateContainer
                {...props}
                setDate={(value) => {
                  onChange(name, value)
                  ref.current.setOpen(false)
                }} />
            ) : null}
            placeholderText={rest.placeholder}
            dateFormat="dd/MM/yyyy"
            readOnly={readOnly}
            className={`${className || dateClass || 'form-input w-100'}${getError() ? ' error' : ''} date-picker${readOnly ? ' disabled' : ''}`}
            selectsRange
            startDate={typeof val[0] === 'string' ? new Date(val[0]) : val[0]}
            endDate={typeof val[1] === 'string' ? new Date(val[1]) : val[1]}
            onChange={(update) => {
              if (every(update, x => x === null)) {
                onChange(name, null)
              } else {
                onChange(name, update)
              }
            }}
            onKeyDown={(e) => {
              e.preventDefault()
            }}
            locale="en-gb"
            {...rest}
          >
          </DatePicker>
          {' '}
          {children}
        </>
      )
    )
  }

  const divRest = useMemo(() => omit(rest, ['minDate', 'maxDate']), [rest])

  return (
    onlyInput ? renderInput() : (
      <div {...divRest} className={eClass || 'mr-8'}>
        {labelKey || label ? (
          <label htmlFor="" className={labelClass || 'form-label'}>
            {' '}
            {labelKey ? t(labelKey) : label}
          </label>
        ) : null}
        <div className="pos-rel">
          {renderInput()}
          {!timePicker && (
            <GHref
              className="date-picker-calendar"
              onClick={() => ref?.current?.setOpen(!ref?.current?.state?.open)}
            >
              <IconCalendar />
            </GHref>
          )}
        </div>
        {getError() && (
          <span className="d-block mt-10">
            <FontAwesomeIcon icon={faCircleExclamation} size="lg" className="color-red" data-tip data-for={name} />
            <ReactTooltip id={name} type="error">
              {getError()}
            </ReactTooltip>
          </span>
        )}
      </div>
    )
  )
}

export default GDatePicker
